import React, { useEffect, useMemo, useRef, useState } from "react";
import "./ObracunObresti.css";

class ObrestiErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, message: "" };
    }

    static getDerivedStateFromError(error) {
        return {
            hasError: true,
            message: error?.message || "Nepričakovana napaka pri obračunu obresti.",
        };
    }

    componentDidCatch(error, info) {
        console.error("[ObrestiErrorBoundary]", error, info);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="oo-alert oo-alert--error" role="alert">
                    Prišlo je do napake pri izračunu obresti. Poskusite osvežiti stran.
                    <br />
                    <small>Podrobnosti: {this.state.message}</small>
                </div>
            );
        }

        return this.props.children;
    }
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function daysBetween(startUTC, endUTC) {
    const s = Date.UTC(
        startUTC.getUTCFullYear(),
        startUTC.getUTCMonth(),
        startUTC.getUTCDate()
    );

    const e = Date.UTC(
        endUTC.getUTCFullYear(),
        endUTC.getUTCMonth(),
        endUTC.getUTCDate()
    );

    return Math.max(0, Math.floor((e - s) / MS_PER_DAY));
}

function addDays(d, n) {
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + n));
}

function parseYMD(ymd) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ""));
    if (!m) return null;

    const y = Number(m[1]);
    const mo = Number(m[2]) - 1;
    const da = Number(m[3]);

    const dt = new Date(Date.UTC(y, mo, da));

    return dt.getUTCFullYear() === y &&
        dt.getUTCMonth() === mo &&
        dt.getUTCDate() === da
        ? dt
        : null;
}

function toYMD(d) {
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, "0");
    const day = String(d.getUTCDate()).padStart(2, "0");

    return `${y}-${m}-${day}`;
}

function formatDisplaySL(d) {
    try {
        return new Intl.DateTimeFormat("sl-SI", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        }).format(d);
    } catch {
        return `${String(d.getUTCDate()).padStart(2, "0")}.${String(d.getUTCMonth() + 1).padStart(2, "0")}.${d.getUTCFullYear()}`;
    }
}

function formatEUR(x) {
    if (!Number.isFinite(x)) return "–";

    try {
        return new Intl.NumberFormat("sl-SI", {
            style: "currency",
            currency: "EUR",
            maximumFractionDigits: 2,
        }).format(x);
    } catch {
        return x.toFixed(2);
    }
}

function round2(x) {
    return Math.round((x + Number.EPSILON) * 100) / 100;
}

function parsePrincipalLoose(value) {
    if (value == null) return 0;

    let s = String(value).trim();
    if (!s) return 0;

    s = s.replace(/[^\d.,-]/g, "");

    const imaVejico = s.includes(",");
    const imaPiko = s.includes(".");

    if (imaVejico && imaPiko) {
        s = s.replace(/\./g, "").replace(",", ".");
    } else if (imaVejico) {
        s = s.replace(",", ".");
    }

    const n = Number(s);
    return Number.isFinite(n) ? n : NaN;
}

const MONTHS_SL = [
    "januar",
    "februar",
    "marec",
    "april",
    "maj",
    "junij",
    "julij",
    "avgust",
    "september",
    "oktober",
    "november",
    "december",
];

const WDAYS_SL = ["Po", "To", "Sr", "Če", "Pe", "So", "Ne"];

const ZOM_FALLBACK = [
    { from: "2025-07-01", to: "2025-12-31", rate: 10.15 },
    { from: "2025-01-01", to: "2025-06-30", rate: 11.15 },
    { from: "2024-07-01", to: "2024-12-31", rate: 12.25 },
    { from: "2024-01-01", to: "2024-06-30", rate: 12.5 },
    { from: "2023-07-01", to: "2023-12-31", rate: 12.0 },
    { from: "2023-01-01", to: "2023-06-30", rate: 10.5 },
    { from: "2016-07-01", to: "2016-12-31", rate: 8.0 },
    { from: "2016-01-01", to: "2016-06-30", rate: 8.05 },
];

async function fetchZomPeriods(signal) {
    const url = "/api/obracun-obresti/zom-rs";

    try {
        const res = await fetch(url, {
            headers: { Accept: "application/json" },
            signal,
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const raw = await res.json();

        const list = (Array.isArray(raw) ? raw : [])
            .map((p) => {
                const from = String(p.from ?? p.From ?? "");
                const to = String(p.to ?? p.To ?? "");
                const rate = Number(p.rate ?? p.Rate);
                const pf = parseYMD(from);
                const pt = parseYMD(to);

                return pf && pt && Number.isFinite(rate) ? { from, to, rate } : null;
            })
            .filter(Boolean)
            .sort((a, b) => (a.from < b.from ? -1 : a.from > b.from ? 1 : 0));

        return list.length
            ? { periods: list, fromApi: true }
            : { periods: ZOM_FALLBACK, fromApi: false };
    } catch {
        return { periods: ZOM_FALLBACK, fromApi: false };
    }
}

function sumInterestZOM(P, sIncl, eIncl, periods) {
    const totalDays = daysBetween(sIncl, addDays(eIncl, 1));

    const parsedPeriods = periods
        .map((p) => {
            const from = parseYMD(p.from);
            const to = parseYMD(p.to);
            const rate = Number(p.rate);

            if (!from || !to || !Number.isFinite(rate)) return null;

            return { from, to, rate };
        })
        .filter(Boolean);

    let interest = 0;
    let coveredDays = 0;
    let chargedDays = 0;
    let interestStopDate = null;

    for (let d = new Date(sIncl.getTime()); d <= eIncl; d = addDays(d, 1)) {
        let rate = null;

        for (const p of parsedPeriods) {
            if (d >= p.from && d <= p.to) {
                rate = p.rate;
                break;
            }
        }

        if (rate != null) coveredDays += 1;

        if (rate == null || P <= 0 || interest >= P) continue;

        const dailyInterest = (P * (rate / 100)) / 365;
        if (!Number.isFinite(dailyInterest) || dailyInterest <= 0) continue;

        interest += dailyInterest;
        chargedDays += 1;

        if (interestStopDate == null && interest >= P - 1e-9) {
            interestStopDate = new Date(d.getTime());
        }
    }

    if (interest > P) interest = P;

    return {
        interest,
        coveredDays,
        chargedDays,
        totalDays,
        interestStopDate,
    };
}

function startOfMonth(d) {
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
}

function daysInMonthUTC(y, m) {
    return new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
}

function mondayIndex(day) {
    return (day + 6) % 7;
}

function Calendar({ value, onSelect, onClose }) {
    const initial = value || new Date();

    const [year, setYear] = useState(initial.getUTCFullYear());
    const [month, setMonth] = useState(initial.getUTCMonth());
    const [focusDate, setFocusDate] = useState(initial);

    const first = startOfMonth(new Date(Date.UTC(year, month, 1)));
    const dim = daysInMonthUTC(year, month);
    const offset = mondayIndex(first.getUTCDay());

    const prevMonth = () => setMonth((m) => (m === 0 ? (setYear((y) => y - 1), 11) : m - 1));
    const nextMonth = () => setMonth((m) => (m === 11 ? (setYear((y) => y + 1), 0) : m + 1));
    const prevYear = () => setYear((y) => y - 1);
    const nextYear = () => setYear((y) => y + 1);

    const ref = useRef(null);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onClose?.();
        };

        const onClickAway = (e) => {
            if (ref.current && !ref.current.contains(e.target)) onClose?.();
        };

        window.addEventListener("keydown", onKey);
        document.addEventListener("mousedown", onClickAway);

        return () => {
            window.removeEventListener("keydown", onKey);
            document.removeEventListener("mousedown", onClickAway);
        };
    }, [onClose]);

    const handleKeyDown = (e) => {
        let delta = 0;

        if (e.key === "ArrowLeft") delta = -1;
        else if (e.key === "ArrowRight") delta = 1;
        else if (e.key === "ArrowUp") delta = -7;
        else if (e.key === "ArrowDown") delta = 7;
        else if (e.key === "Home") {
            const d = new Date(Date.UTC(year, month, 1));
            setFocusDate(d);
            e.preventDefault();
            return;
        } else if (e.key === "End") {
            const d = new Date(Date.UTC(year, month, dim));
            setFocusDate(d);
            e.preventDefault();
            return;
        } else if (e.key === "PageUp") {
            prevMonth();
            e.preventDefault();
            return;
        } else if (e.key === "PageDown") {
            nextMonth();
            e.preventDefault();
            return;
        } else if (e.key === "Enter") {
            onSelect(focusDate);
            e.preventDefault();
            return;
        }

        if (delta !== 0) {
            const nd = addDays(focusDate, delta);
            setFocusDate(nd);

            if (nd.getUTCFullYear() !== year || nd.getUTCMonth() !== month) {
                setYear(nd.getUTCFullYear());
                setMonth(nd.getUTCMonth());
            }

            e.preventDefault();
        }
    };

    const cells = [];
    const todayYMD = toYMD(new Date());
    const selectedYMD = value ? toYMD(value) : "";
    const focusYMD = toYMD(focusDate);

    const totalCells = Math.ceil((offset + dim) / 7) * 7;

    for (let i = 0; i < totalCells; i++) {
        const dayNum = i - offset + 1;
        const inMonth = dayNum >= 1 && dayNum <= dim;
        const date = inMonth ? new Date(Date.UTC(year, month, dayNum)) : null;
        const ymd = date ? toYMD(date) : "";
        const isSelected = selectedYMD && ymd === selectedYMD;
        const isToday = date && ymd === todayYMD;

        cells.push(
            <button
                key={i}
                className={[
                    "oo-cal-cell",
                    inMonth ? "is-in" : "is-out",
                    isSelected ? "is-sel" : "",
                    isToday ? "is-today" : "",
                ]
                    .join(" ")
                    .trim()}
                type="button"
                disabled={!inMonth}
                onClick={() => inMonth && onSelect(date)}
                aria-label={inMonth ? formatDisplaySL(date) : undefined}
                tabIndex={inMonth && ymd === focusYMD ? 0 : -1}
            >
                {inMonth ? dayNum : ""}
            </button>
        );
    }

    return (
        <div
            className="oo-date-pop"
            role="dialog"
            aria-modal="true"
            aria-label="Izbirnik datuma"
            ref={ref}
            onKeyDown={handleKeyDown}
        >
            <div className="oo-cal">
                <div className="oo-cal-head">
                    <button type="button" className="oo-cal-nav oo-link" onClick={prevYear} aria-label="Prejšnje leto">
                        «
                    </button>

                    <button type="button" className="oo-cal-nav oo-link" onClick={prevMonth} aria-label="Prejšnji mesec">
                        ‹
                    </button>

                    <div className="oo-cal-title">
                        {MONTHS_SL[month]} {year}
                    </div>

                    <button type="button" className="oo-cal-nav oo-link" onClick={nextMonth} aria-label="Naslednji mesec">
                        ›
                    </button>

                    <button type="button" className="oo-cal-nav oo-link" onClick={nextYear} aria-label="Naslednje leto">
                        »
                    </button>
                </div>

                <div className="oo-cal-wdays">
                    {WDAYS_SL.map((d) => (
                        <div key={d} className="oo-cal-wday">
                            {d}
                        </div>
                    ))}
                </div>

                <div className="oo-cal-grid">{cells}</div>

                <div className="oo-cal-actions">
                    <button type="button" className="oo-cal-link oo-link" onClick={() => onSelect(new Date())}>
                        Danes
                    </button>

                    <button type="button" className="oo-cal-link oo-link" onClick={onClose}>
                        Zapri
                    </button>
                </div>
            </div>
        </div>
    );
}

function useElementSize(elRef) {
    const [size, setSize] = useState({ width: 0, height: 0 });

    useEffect(() => {
        const el = elRef.current;
        if (!el) return;

        const update = () => setSize({ width: el.offsetWidth, height: el.offsetHeight });
        update();

        if (typeof ResizeObserver !== "undefined") {
            const ro = new ResizeObserver(update);
            ro.observe(el);
            return () => ro.disconnect();
        }

        window.addEventListener("resize", update);
        return () => window.removeEventListener("resize", update);
    }, [elRef]);

    return size;
}

function DateField({ label, value, onChange, min, max, id }) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    const inputRef = useRef(null);
    const { height } = useElementSize(inputRef);

    useEffect(() => {
        const onClickAway = (e) => {
            if (!wrapRef.current?.contains(e.target)) setOpen(false);
        };

        document.addEventListener("mousedown", onClickAway);

        return () => document.removeEventListener("mousedown", onClickAway);
    }, []);

    const dateObj = value ? parseYMD(value) : null;
    const display = dateObj ? formatDisplaySL(dateObj) : "";

    const handleSelect = (d) => {
        const ymd = toYMD(d);

        if (min && ymd < min) return;
        if (max && ymd > max) return;

        onChange(ymd);
        setOpen(false);
    };

    const btnSize = Math.max(26, Math.min(36, Math.round(height * 0.66)));
    const iconSize = Math.round(btnSize * 0.72);
    const padRight = btnSize + 16;

    return (
        <div className="oo-field">
            <label htmlFor={id}>{label}</label>

            <div
                className="oo-date-wrap"
                ref={wrapRef}
                style={{
                    "--oo-date-icon-size": `${iconSize}px`,
                    "--oo-date-pad-right": `${padRight}px`,
                }}
            >
                <input
                    id={id}
                    ref={inputRef}
                    type="text"
                    className="oo-date-input oo-date-input--with-icon"
                    value={display}
                    placeholder="Kliknite za izbor datuma"
                    readOnly
                    onClick={() => setOpen((v) => !v)}
                    aria-label={label}
                    aria-haspopup="dialog"
                    aria-expanded={open ? "true" : "false"}
                />

                {open && (
                    <Calendar
                        value={dateObj || new Date()}
                        onSelect={handleSelect}
                        onClose={() => setOpen(false)}
                    />
                )}
            </div>
        </div>
    );
}

function rateForDate(ymd, periods) {
    const d = parseYMD(ymd);
    if (!d) return null;

    for (const p of periods) {
        const ps = parseYMD(p.from);
        const pe = parseYMD(p.to);

        if (ps && pe && d >= ps && d <= pe) {
            return Number(p.rate);
        }
    }

    return null;
}

// memory only, no localStorage
let lastPrincipalSum = 0;

export function getLastPrincipalSum() {
    return lastPrincipalSum;
}

function ObracunObrestiInner({ onCalculated, initialPrincipal }) {
    const localNow = new Date();
    const localY = localNow.getFullYear();
    const localM = localNow.getMonth();
    const localD = localNow.getDate();

    const todayUTC = new Date(Date.UTC(localY, localM, localD));
    const weekAgoUTC = addDays(todayUTC, -7);

    const [principals, setPrincipals] = useState(() => {
        if (initialPrincipal != null && String(initialPrincipal).trim() !== "") {
            return [String(initialPrincipal)];
        }

        return [""];
    });

    useEffect(() => {
        if (initialPrincipal != null && String(initialPrincipal).trim() !== "") {
            setPrincipals([String(initialPrincipal)]);
        } else {
            setPrincipals([""]);
        }
    }, [initialPrincipal]);

    const [startDate, setStartDate] = useState(toYMD(weekAgoUTC));
    const [endDate, setEndDate] = useState(toYMD(todayUTC));

    const [{ periods, fromApi }, setPeriodsState] = useState({
        periods: ZOM_FALLBACK,
        fromApi: false,
    });

    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        const ac = new AbortController();

        (async () => {
            const res = await fetchZomPeriods(ac.signal);
            setPeriodsState(res);
            setLoaded(true);
        })();

        return () => ac.abort();
    }, []);

    const currentRateDateYMD = toYMD(todayUTC);
    const currentRateDateDisplay = formatDisplaySL(todayUTC);

    const currentRate = useMemo(
        () => rateForDate(currentRateDateYMD, periods),
        [currentRateDateYMD, periods]
    );

    const invalid = (message, principalValue, extra = {}) => ({
        valid: false,
        message,
        days: 0,
        interest: 0,
        total: principalValue ?? 0,
        interestStopDate: null,
        principalSum: principalValue ?? 0,
        ...extra,
    });

    const result = useMemo(() => {
        const s = parseYMD(startDate);
        const e = parseYMD(endDate);

        if (!s || !e) {
            return invalid("Vnesite oba datuma v pravilni obliki.", 0);
        }

        if (e < s) {
            return invalid("Končni datum ne sme biti pred začetnim datumom.", 0);
        }

        const parsed = principals.map((p) => Math.max(0, parsePrincipalLoose(p)));

        if (parsed.some((p) => !Number.isFinite(p))) {
            return invalid("Vnesite znesek glavnice v evrih.", 0);
        }

        const probe = sumInterestZOM(0, s, e, periods);
        const totalDays = probe.totalDays;

        let sumInterest = 0;
        let sumPrincipal = 0;
        let coveredDays = 0;
        let earliestStop = null;

        for (const P of parsed) {
            sumPrincipal += P;

            const r = sumInterestZOM(P, s, e, periods);

            coveredDays = Math.max(coveredDays, r.coveredDays);
            sumInterest += r.interest;

            if (r.interestStopDate) {
                if (!earliestStop || r.interestStopDate < earliestStop) {
                    earliestStop = r.interestStopDate;
                }
            }
        }

        if (coveredDays < totalDays) {
            return invalid(
                "Za del izbranega obdobja ni objavljene zakonske zamudne obrestne mere (ZOM). Izberite krajše obdobje.",
                sumPrincipal,
                { days: totalDays }
            );
        }

        const interestRounded = round2(sumInterest);
        const totalRounded = round2(sumPrincipal + interestRounded);

        return {
            valid: true,
            days: totalDays,
            interest: interestRounded,
            total: totalRounded,
            interestStopDate: earliestStop,
            principalSum: sumPrincipal,
        };
    }, [principals, startDate, endDate, periods]);

    useEffect(() => {
        const sum = Number(result?.principalSum ?? 0);

        if (!Number.isFinite(sum) || sum <= 0) {
            return;
        }

        lastPrincipalSum = sum;

        if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("principal:sum", { detail: sum }));
        }

        if (typeof onCalculated === "function") {
            onCalculated({
                principalSum: sum,
                interest: result.interest,
                total: result.total,
                days: result.days,
                interestStopDate: result.interestStopDate,
                startDate,
                endDate,
            });
        }
    }, [result, onCalculated, startDate, endDate]);

    const reset = () => {
        const t = todayUTC;
        setPrincipals([""]);
        setStartDate(toYMD(addDays(t, -7)));
        setEndDate(toYMD(t));
    };

    const onBtnMove = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        e.currentTarget.style.setProperty("--mx", `${x}%`);
    };

    const setPrincipalAt = (idx, val) => {
        setPrincipals((arr) => arr.map((x, i) => (i === idx ? val : x)));
    };

    const addPrincipal = () => {
        setPrincipals((arr) => [...arr, ""]);
    };

    const removePrincipal = (idx) => {
        setPrincipals((arr) =>
            arr.length <= 1 ? [""] : arr.filter((_, i) => i !== idx)
        );
    };

    return (
        <div className="oo-wrap">
            <div className="oo-card oo-card--screen">
                {!result.valid && (
                    <div className="oo-alert oo-alert--info" role="alert">
                        {result.message}
                    </div>
                )}

                {!loaded && (
                    <div className="oo-alert oo-alert--info" role="status">
                        Nalaganje obrestnih stopenj … trenutno se uporablja vgrajen seznam.
                    </div>
                )}

                {loaded && !fromApi && (
                    <div className="oo-alert oo-alert--warning" role="alert">
                        Zunanji vir stopenj ni dosegljiv – uporablja se vgrajen seznam zakonskih zamudnih obrestnih mer.
                    </div>
                )}

                <div className="oo-grid">
                    <div className="oo-field">
                        <label>Glavnica (znesek v EUR)</label>

                        <div className="oo-principals">
                            {principals.map((val, idx) => (
                                <div className="oo-principal-row" key={idx}>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={val}
                                        onChange={(e) => setPrincipalAt(idx, e.target.value)}
                                        placeholder="npr. 1000.00"
                                        aria-label={`Glavnica ${idx + 1} v evrih`}
                                    />

                                    <button
                                        type="button"
                                        className="oo-link oo-principal-remove"
                                        onClick={() => removePrincipal(idx)}
                                        aria-label={`Odstrani glavnico ${idx + 1}`}
                                    >
                                        Odstrani
                                    </button>
                                </div>
                            ))}

                            <button
                                type="button"
                                className="oo-link oo-principal-add"
                                onClick={addPrincipal}
                                aria-label="Dodaj glavnico"
                            >
                                + Dodaj glavnico
                            </button>
                        </div>
                    </div>

                    <div className="oo-field">
                        <label>
                            Trenutna zakonska zamudna obrestna mera{" "}
                            <a
                                className="oo-link"
                                href="https://www.bsi.si/sl/statistika/obrestne-mere/temeljna-in-zamudna-obrestna-mera"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Odpri vir Banka Slovenije v novem zavihku"
                            >
                                (vir: Banka Slovenije)
                            </a>
                        </label>

                        <div className="oo-ro">
                            <span>{currentRate != null ? `${currentRate.toFixed(2)} %` : "–"}</span>
                            <span className="oo-ro-sub">velja za dan {currentRateDateDisplay}</span>
                        </div>
                    </div>

                    <DateField
                        id="oo-date-start"
                        label="Začetni datum"
                        value={startDate}
                        onChange={setStartDate}
                    />

                    <DateField
                        id="oo-date-end"
                        label="Končni datum (vključno)"
                        value={endDate}
                        onChange={setEndDate}
                    />
                </div>

                <div className="oo-stats">
                    <div className="oo-stat">
                        <div className="oo-stat-label">Število dni</div>
                        <div className="oo-stat-value">{result.days}</div>
                    </div>

                    <div className="oo-stat">
                        <div className="oo-stat-label">Obresti</div>
                        <div className="oo-stat-value">{formatEUR(result.interest)}</div>
                    </div>

                    <div className="oo-stat">
                        <div className="oo-stat-label">Skupaj (glavnice + obresti)</div>
                        <div className="oo-stat-value">{formatEUR(result.total)}</div>
                    </div>
                </div>

                {result.valid && result.interestStopDate && (
                    <div className="oo-alert oo-alert--info" role="status">
                        Obresti dosežejo višino glavnice dne {formatDisplaySL(result.interestStopDate)}. Po tem datumu se več ne obračunavajo (STOP).
                    </div>
                )}

                <div className="oo-actions">
                    <button type="button" className="oo-btn" onClick={reset} onMouseMove={onBtnMove}>
                        Nov izračun
                    </button>
                </div>
            </div>

            <div className="oo-printSummary">
                <div className="oo-printRow">
                    <span className="oo-printLabel">Glavnica (znesek v EUR)</span>
                    <span className="oo-printValue">{formatEUR(result.principalSum ?? 0)}</span>
                </div>

                <div className="oo-printRow">
                    <span className="oo-printLabel">Zakonske zamudne obresti</span>
                    <span className="oo-printValue">{formatEUR(result.interest)}</span>
                </div>

                <div className="oo-printRow">
                    <span className="oo-printLabel">Skupaj (glavnice + obresti)</span>
                    <span className="oo-printValue">{formatEUR(result.total)}</span>
                </div>
            </div>
        </div>
    );
}

export default function ObracunObresti(props) {
    return (
        <ObrestiErrorBoundary>
            <ObracunObrestiInner {...props} />
        </ObrestiErrorBoundary>
    );
}